import * as React from 'react';
import {useSelector} from 'react-redux';

import {formValueSelector} from 'redux-form';
// @ts-expect-error
import type {FieldProps} from 'redux-form';

import type {DisplayOptionEntity} from 'nutshell-core/utils';

import {Avatar} from 'shells/avatar/avatar';
import {IconAvatar} from 'shells/avatar/icon-avatar';
import {colors} from 'shells/colors/colors';
import {ContactIcon} from 'shells/icon/svg/contact-icon';
import {TrashIcon} from 'shells/icon/svg/trash-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {EntityPageAddItemButton} from 'shells/lead-sidebar/entity-page-add-item-button';
import {ContactCard} from 'shells/peep-cards/contact-card/contact-card';
import {ContactCardCreateable} from 'shells/peep-cards/contact-card/contact-card-createable/contact-card-createable';
import type {ContactInput} from 'shells/peep-cards/contact-card/types';

import type {ContactCreateValidationInput} from 'nutshell-graphql-types';

import {LEADS_FORM_KEY} from '../../../../../entity-form/forms/lead-form-connected';
import {useGetContactFormValidation} from '../../../../graphql/hooks';
import {useValidatedAvatarUrls} from '../../../../hooks/use-validated-avatar-url';

import {contactInputToDisplayOptionEntity} from './helpers';
import {getPotentialContacts} from './rich-contact-select-helpers';

const leadFormSelector = formValueSelector(LEADS_FORM_KEY);

const NEW_CONTACT_MAGIC_ID = 'new-contact-magic-id';

type Props = FieldProps;

export function RichContactSelect(props: Props): React.ReactElement {
    const {input} = props;

    const [isEditingContactId, setIsEditingContactId] = React.useState<string | null | undefined>(
        null
    );
    const validatedAvatarUrls = useValidatedAvatarUrls(input.value || []);

    const alreadyAddedContactsIds = React.useMemo(
        // @ts-expect-error
        () => (Array.isArray(input.value) ? input.value.map((c) => c.id) : []),
        [input.value]
    );

    const {refetch: fetchFormValidation} = useGetContactFormValidation();

    // @ts-expect-error
    const selectedAccounts = useSelector((state) => leadFormSelector(state, 'links.accounts'));
    const selectedAccountIds =
        selectedAccounts &&
        // @ts-expect-error
        selectedAccounts.filter((account) => !account.isNew).map((account) => account.id);

    const handleCreateContact = (contactInput: ContactInput) => {
        input.onChange([
            ...input.value,
            contactInputToDisplayOptionEntity(
                contactInput,
                true,
                String(Date.now()) + String(Math.random())
            ),
        ]);

        setIsEditingContactId(null);
    };

    const handleUpdateContact = (contactInput: ContactInput) => {
        const updatedContact = contactInputToDisplayOptionEntity(contactInput, true);

        input.onChange(
            // @ts-expect-error
            input.value.map((contact) =>
                contact.id === isEditingContactId ? {id: contact.id, ...updatedContact} : contact
            )
        );
        setIsEditingContactId(null);
    };

    const handleSelectContact = (contact: DisplayOptionEntity) => {
        input.onChange([...input.value, contact]);
        setIsEditingContactId(null);
    };

    const handleRemoveContact = (id?: string | null) => {
        // @ts-expect-error
        input.onChange(input.value.filter((contact) => contact.id !== id));
        setIsEditingContactId(null);
    };

    return (
        <StackLegacy spacing={12}>
            {input.value &&
                // @ts-expect-error
                input.value.map((contact) => {
                    const avatarUrlValidated = validatedAvatarUrls[contact.id];

                    if (contact.id === isEditingContactId) {
                        return (
                            <ContactCardCreateable
                                key={contact.id}
                                onSelectContact={handleSelectContact}
                                onSubmit={handleUpdateContact}
                                onClose={() => setIsEditingContactId(null)}
                                validate={(contactInput: ContactCreateValidationInput) =>
                                    fetchFormValidation(contactInput)
                                }
                                temporaryContact={{
                                    id: contact.id,
                                    name: contact.value,
                                    description: contact.entityDescription,
                                    phoneNumber: contact.primaryPhoneText,
                                    email: contact.primaryEmailText,
                                    jobTitle: contact.jobTitle,
                                }}
                                hasRelatedAccounts={false}
                            />
                        );
                    }

                    return (
                        <ContactCard
                            key={contact.id}
                            name={contact.value}
                            description={contact.entityDescription}
                            phoneNumber={contact.primaryPhoneText}
                            email={contact.primaryEmailText}
                            role={contact.jobTitle}
                            actionMenuOptions={[
                                {
                                    label: 'Remove',
                                    icon: <TrashIcon size={12} wrapWithDiv={false} />,
                                    handleSelect: () => handleRemoveContact(contact.id),
                                },
                            ]}
                            tertiaryProps={
                                contact.isNew
                                    ? {
                                          type: 'text-button',
                                          text: 'Edit',
                                          onClick: () => setIsEditingContactId(contact.id),
                                      }
                                    : undefined
                            }
                            icon={
                                <>
                                    {avatarUrlValidated ? (
                                        <Avatar
                                            avatarUrl={avatarUrlValidated}
                                            size={32}
                                            circular={false}
                                            hasBorderRadius={true}
                                        />
                                    ) : (
                                        <IconAvatar
                                            icon={ContactIcon}
                                            avatarSize={32}
                                            iconSize={16}
                                            iconColor={colors.person}
                                            backgroundColor={colors.blueXlt}
                                            shape='square'
                                        />
                                    )}
                                </>
                            }
                        />
                    );
                })}
            {isEditingContactId === NEW_CONTACT_MAGIC_ID && (
                <ContactCardCreateable
                    onSelectContact={handleSelectContact}
                    onSubmit={handleCreateContact}
                    onClose={() => setIsEditingContactId(null)}
                    validate={(contactInput: ContactCreateValidationInput) =>
                        fetchFormValidation(contactInput)
                    }
                    onSearchPotentialContacts={(searchInput: ContactInput) =>
                        getPotentialContacts({
                            contactInput: searchInput,
                            relatedAccountIds: selectedAccountIds,
                            alreadyAddedContactsIds,
                        })
                    }
                    hasRelatedAccounts={Boolean(selectedAccountIds)}
                />
            )}
            {!isEditingContactId && (
                <EntityPageAddItemButton
                    sectionEntity='contacts'
                    parentItemText='a person'
                    onAddItem={() => {
                        setIsEditingContactId(NEW_CONTACT_MAGIC_ID);
                    }}
                    omitEntityName={true}
                />
            )}
        </StackLegacy>
    );
}
